import React, { useEffect, useMemo } from 'react';
import { useVaults } from '../api/getAllVaults';
import { VaultCard } from './VaultCard';
import { VaultCardListClean } from './VaultCardListClean';
import { VaultCardListCompact } from './VaultCardListCompact';
import { VaultSkeleton } from './VaultSkeleton';
import { FiEye } from 'react-icons/fi';

const StatusBadge = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30' },
    frozen: { label: 'Frozen', cls: 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/30' },
    scheduled_closure: { label: 'Scheduled Closure', cls: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30' },
    closed: { label: 'Closed', cls: 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/30' },
  };
  const m = map[status] || { label: status || 'Unknown', cls: 'bg-white/10 text-white/80 ring-1 ring-white/20' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.cls}`}>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {m.label}
    </span>
  );
};

export const VaultsList = ({
  pageNumber = 1,
  searchText = '',
  filters = {},
  displayMode = 'grid',
  setTotalPages = () => {},
  setTotalItems = () => {},
  /** NEW: called when a vault (card/row) is clicked */
  onVaultClick,
  /** NEW: optional custom badge renderer (overrides default) */
  renderVaultBadge,
}) => {
  const { data, isLoading, isError } = useVaults({ pageNumber });

  const filteredVaults = useMemo(() => {
    if (!data?.data?.vaults) return [];
    const lowerSearch = (searchText || '').toLowerCase();

    return data.data.vaults.filter((vault) => {
      const matchesSearch =
        (vault.vaultAlias?.toLowerCase().includes(lowerSearch)) ||
        (vault.owner?.toLowerCase().includes(lowerSearch)) ||
        (vault.email?.toLowerCase().includes(lowerSearch)) ||
        (vault.id?.toLowerCase().includes(lowerSearch)) ||
        (vault.wireId?.toLowerCase?.().includes(lowerSearch));

      const matchesFilters =
        (!filters.alias || vault.vaultAlias?.toLowerCase().includes(filters.alias.toLowerCase())) &&
        (!filters.user || vault.owner?.toLowerCase().includes(filters.user.toLowerCase())) &&
        (!filters.currency || vault.currencyCode?.toLowerCase().includes(filters.currency.toLowerCase())) &&
        (!filters.country || vault.country?.toLowerCase().includes(filters.country.toLowerCase())) &&
        (!filters.status || (vault.status || '').toLowerCase() === filters.status.toLowerCase());

      return matchesSearch && matchesFilters;
    });
  }, [data, searchText, filters]);

  useEffect(() => {
    if (data?.data?.totalPages) setTotalPages(data.data.totalPages);
    if (data?.data?.totalItems) setTotalItems(data.data.totalItems);
  }, [data, setTotalPages, setTotalItems]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <VaultSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return <div className="text-red-500 text-center">Failed to load vaults.</div>;
  }

  if (filteredVaults.length === 0) {
    return <div className="text-white/60 text-center py-10">No vaults match the filters/search.</div>;
  }

  const Badge = ({ v }) => (renderVaultBadge ? renderVaultBadge(v) : <StatusBadge status={v?.status} />);

  if (displayMode === 'grid') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVaults.map((vault) => (
          <div key={vault.id} className="group relative">
            {/* overlay quick-peek button on hover */}
            <button
              onClick={() => onVaultClick?.(vault)}
              className="absolute right-2 top-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity
                         inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-black/40 hover:bg-black/60"
              aria-label={`Quick peek ${vault.vaultAlias ?? vault.id}`}
            >
              <FiEye /> Peek
            </button>

            {/* your existing card; make entire card clickable too */}
            <div
              onClick={() => onVaultClick?.(vault)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onVaultClick?.(vault)}
              className="cursor-pointer"
            >
              <VaultCard
                vault={vault}
                endAdornment={<Badge v={vault} />}
                // If your VaultCard supports a custom footer/flags, pass more props here
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (displayMode === 'list-clean') {
    return (
      <div className="flex flex-col gap-3">
        {filteredVaults.map((vault) => (
          <VaultCardListClean
            key={vault.id}
            vault={vault}
            badge={<Badge v={vault} />}
            onClick={() => onVaultClick?.(vault)}
          />
        ))}
      </div>
    );
  }

  if (displayMode === 'list-compact') {
    return (
      <div className="flex flex-col divide-y divide-white/10">
        {filteredVaults.map((vault) => (
          <VaultCardListCompact
            key={vault.id}
            vault={vault}
            badge={<Badge v={vault} />}
            onClick={() => onVaultClick?.(vault)}
          />
        ))}
      </div>
    );
  }

  return null;
};
